/**
 * src/components/auth/SpaceMembersModal.tsx
 * =============================================================================
 * Gestiona los miembros de un espacio compartido. Solo aparece para admins.
 *
 * Contenido:
 *   - Lista de miembros (admin + collaborators) con avatar inicial.
 *   - Lista de invitaciones pending con botón × revocar.
 *   - Form para añadir miembro por email (genera invite + email Resend).
 *   - Botón "Abandonar espacio" — solo si NO es admin único (TODO).
 *
 * Mismo lenguaje visual que ShareModal — el patrón "lista + form invitar +
 * sección secundaria" ya está validado.
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Check, Copy, Loader2, Mail, X } from 'lucide-react'
import {
  inviteToSpace,
  listSpaceMembers,
  removeSpaceMember,
  revokeSpaceInvitation,
  SpacesError,
  type SpaceInvitation,
  type SpaceInviteResult,
  type SpaceListItem,
  type SpaceMember,
} from '@/services/spacesService'
import { getCheckout, getQuota, type QuotaInfo } from '@/services/billingService'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { useActiveSpace } from '@/components/providers/ActiveSpaceProvider'
import { useAuth } from '@/components/providers/AuthProvider'

interface Props {
  space: SpaceListItem
  onClose: () => void
}

export function SpaceMembersModal({ space, onClose }: Props) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { refresh } = useActiveSpace()
  const [members, setMembers] = useState<SpaceMember[]>([])
  const [invitations, setInvitations] = useState<SpaceInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [lastInvite, setLastInvite] = useState<SpaceInviteResult | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [removingUserId, setRemovingUserId] = useState<string | null>(null)
  const [quota, setQuota] = useState<QuotaInfo | null>(null)
  const [upgrading, setUpgrading] = useState(false)
  /** Trampa de foco: Tab cicla dentro y al cerrar vuelve al disparador. */
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef)

  // `mountedRef` se mantiene en true mientras el modal está montado. Las
  // funciones async (`reload`, `onInvite`, `onRemove`, etc.) lo consultan
  // antes de setear state — evita warnings "setState on unmounted" cuando
  // el user cierra el modal antes de que llegue la respuesta. Más simple
  // que pasar un `cancelled` por cada call site.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const reload = useCallback(() => {
    void Promise.all([
      listSpaceMembers(space.id).catch(() => ({ members: [], invitations: [] })),
      getQuota(space.id).catch(() => null),
    ]).then(([r, q]) => {
      if (!mountedRef.current) return
      setMembers(r.members)
      setInvitations(r.invitations)
      setQuota(q)
      setLoading(false)
    })
  }, [space.id])

  async function onUpgrade(plan: 'space_base') {
    if (upgrading) return
    setUpgrading(true)
    try {
      const r = await getCheckout(plan, { spaceId: space.id })
      if (r.url) window.location.href = r.url
    } catch (err) {
      console.error('space upgrade checkout failed', err)
    } finally {
      if (mountedRef.current) setUpgrading(false)
    }
  }

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function onInvite(e: FormEvent) {
    e.preventDefault()
    if (inviting) return
    const trimmed = inviteEmail.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setInviteError('invalid_email')
      return
    }
    setInviting(true)
    setInviteError(null)
    try {
      const r = await inviteToSpace(space.id, trimmed)
      if (!mountedRef.current) return
      setLastInvite(r)
      setInviteEmail('')
      reload()
    } catch (err) {
      if (!mountedRef.current) return
      const code = err instanceof SpacesError ? err.code : 'request_failed'
      setInviteError(code)
    } finally {
      if (mountedRef.current) setInviting(false)
    }
  }

  async function onRevoke(invitationId: string) {
    setRevokingId(invitationId)
    try {
      await revokeSpaceInvitation(invitationId)
      if (!mountedRef.current) return
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId))
    } catch (err) {
      console.error('revoke failed', err)
    } finally {
      if (mountedRef.current) setRevokingId(null)
    }
  }

  async function onRemove(userId: string) {
    setRemovingUserId(userId)
    try {
      await removeSpaceMember(space.id, userId)
      if (!mountedRef.current) return
      setMembers((prev) => prev.filter((m) => m.userId !== userId))
      // refrescar context: el memberCount baja
      void refresh()
    } catch (err) {
      console.error('remove failed', err)
    } finally {
      if (mountedRef.current) setRemovingUserId(null)
    }
  }

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('panel.miembros')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md max-h-[85dvh] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 h-14 border-b border-zinc-100 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-zinc-900 truncate">
              {space.name}
            </h2>
            <p className="text-[11px] text-zinc-500">{t('panel.miembros')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close')}
            className="h-8 w-8 flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          >
            <X className="h-5 w-5" strokeWidth={1.8} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Banner de quota — miembros usados/max + GB usados. Solo aparece
              cuando la quota está cargada. Si el espacio no está pagado
              (isPaidSpace=false), muestra CTA "Activar espacio (€5/mes)". */}
          {quota && (
            <SpaceQuotaBanner
              quota={quota}
              currentMembers={members.length + invitations.length}
              onUpgrade={onUpgrade}
              upgrading={upgrading}
            />
          )}

          {/* Form invitar */}
          <form onSubmit={(e) => void onInvite(e)} className="space-y-2">
            <div className="flex gap-2">
              <input
                type="email"
                // La trampa de foco arranca aquí: invitar es la acción
                // primaria del modal, no la X de cerrar.
                data-autofocus
                placeholder={t('invitaciones.emailPlaceholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviting}
                autoComplete="off"
                className="flex-1 h-9 px-3 rounded-md border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
              />
              <button
                type="submit"
                disabled={inviting || inviteEmail.trim().length === 0}
                className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
              >
                {inviting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />}
                {t('actions.invitar')}
              </button>
            </div>
            {inviteError && (
              <p className="text-xs text-red-600" role="alert">
                {invitErrorMessage(t, inviteError)}
              </p>
            )}
          </form>

          {lastInvite && <LastInviteNotice invite={lastInvite} />}

          {/* Lista miembros */}
          <section>
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
              {t('miembrosEspacio.titulo', { count: members.length })}
            </h3>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              </div>
            ) : (
              <ul className="space-y-1.5">
                {members.map((m) => {
                  const isMe = m.userId === user?.id
                  const isAdmin = m.role === 'admin'
                  const canRemove = !isAdmin && !isMe
                  return (
                    <li
                      key={m.userId}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-zinc-50"
                    >
                      <div
                        className="h-7 w-7 shrink-0 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-medium"
                        aria-hidden
                      >
                        {(m.name ?? m.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-zinc-900 truncate">
                          {m.name ? `${m.name} ` : ''}
                          <span className={m.name ? 'text-zinc-500 font-normal' : ''}>
                            {m.email}
                          </span>
                          {isMe && (
                            <span className="text-[10px] text-zinc-400 ml-1">{t('miembrosEspacio.tu')}</span>
                          )}
                        </p>
                        <p className="text-[11px] text-zinc-500">
                          {isAdmin ? t('miembrosEspacio.rolAdmin') : t('miembrosEspacio.rolColaborador')}
                        </p>
                      </div>
                      {canRemove && (
                        <button
                          type="button"
                          onClick={() => void onRemove(m.userId)}
                          disabled={removingUserId === m.userId}
                          aria-label={t('miembrosEspacio.expulsar')}
                          title={t('miembrosEspacio.expulsarTitulo')}
                          className="h-7 w-7 shrink-0 flex items-center justify-center rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {removingUserId === m.userId ? (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                          ) : (
                            <X className="h-4 w-4" strokeWidth={1.8} />
                          )}
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {/* Lista invitaciones pendientes */}
          {invitations.length > 0 && (
            <section className="pt-2 border-t border-zinc-100">
              <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
                {t('miembrosEspacio.invitacionesPendientes', { count: invitations.length })}
              </h3>
              <ul className="space-y-1.5">
                {invitations.map((inv) => (
                  <li
                    key={inv.id}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-zinc-50"
                  >
                    <div
                      className="h-7 w-7 shrink-0 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center text-xs font-medium"
                      aria-hidden
                    >
                      {inv.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-900 truncate">{inv.email}</p>
                      <p className="text-[11px] text-zinc-500">{t('invitaciones.pendiente')}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void onRevoke(inv.id)}
                      disabled={revokingId === inv.id}
                      aria-label={t('invitaciones.revocarInvitacion')}
                      title={t('invitaciones.revocarInvitacion')}
                      className="h-7 w-7 shrink-0 flex items-center justify-center rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {revokingId === inv.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                      ) : (
                        <X className="h-4 w-4" strokeWidth={1.8} />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}

function LastInviteNotice({ invite }: { invite: SpaceInviteResult }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  function copy() {
    void navigator.clipboard.writeText(invite.acceptUrl).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }
  if (invite.emailSent) {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
        <Mail className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span>
          {/* Trans mantiene la negrita del email respetando el orden de
              palabras de cada idioma. */}
          <Trans
            i18nKey="invitaciones.emailEnviado"
            values={{ email: invite.email }}
            components={{ b: <strong /> }}
          />
        </span>
      </div>
    )
  }
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-1.5">
      <p className="text-xs text-amber-900">
        <Trans
          i18nKey="invitaciones.emailFallo"
          values={{ email: invite.email }}
          components={{ b: <strong /> }}
        />
      </p>
      <p className="text-[11px] font-mono text-zinc-700 break-all">{invite.acceptUrl}</p>
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-1.5 text-xs px-2 h-7 rounded-md border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-100"
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
            {t('visor.compartir.copiado')}
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
            {t('invitaciones.copiarEnlace')}
          </>
        )}
      </button>
    </div>
  )
}

/**
 * Traduce el código de error del invite a una frase, vía las claves
 * `miembrosEspacio.errores.*`. `space_full` ya no lo devuelve el backend
 * (miembros ilimitados desde el 2026-08-14); se mantiene por si alguna
 * pestaña vieja sigue abierta contra la versión anterior del Worker.
 * Códigos sin clave caen en `generic`.
 */
const CODIGOS_INVITE_ESPACIO = new Set([
  'invalid_email',
  'pro_required',
  'space_upgrade_required',
  'space_full',
  'not_space_admin',
  'forbidden',
  'rate_limited',
])

function invitErrorMessage(t: TFunction, code: string): string {
  const clave = CODIGOS_INVITE_ESPACIO.has(code) ? code : 'generic'
  return t(`miembrosEspacio.errores.${clave}`)
}

// ─── Banner de quota + upgrade ────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(0)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function SpaceQuotaBanner({
  quota,
  currentMembers,
  onUpgrade,
  upgrading,
}: {
  quota: QuotaInfo
  currentMembers: number
  onUpgrade: (plan: 'space_base') => void
  upgrading: boolean
}) {
  const { t } = useTranslation()
  // Espacio pagado pero sin ningún admin con Pro. Se distingue del "nunca
  // activado" a propósito: son problemas distintos y la acción también.
  // Aquí NO se ofrece pagar otra vez — ya está pagado; lo que falta es
  // recuperar el Pro. Ofrecer un botón de pago sería cobrar dos veces por lo
  // mismo. Y se dice explícitamente que no se ha borrado nada, que es lo
  // primero que uno teme al ver un aviso así.
  if (quota.ownerProMissing) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
        <p className="text-sm text-amber-900 font-medium">
          {t('miembrosEspacio.soloLectura.titulo')}
        </p>
        <p className="text-xs text-amber-900/80 leading-relaxed">
          <Trans
            i18nKey="miembrosEspacio.soloLectura.texto"
            components={{ b: <strong /> }}
          />
        </p>
        <Link
          to="/precios"
          className="inline-block text-xs font-medium text-amber-900 underline underline-offset-4"
        >
          {t('actions.verPlanesFlecha')}
        </Link>
      </div>
    )
  }

  // Espacio compartido que nunca llegó a contratarse.
  if (!quota.isPaidSpace) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
        <p className="text-sm text-amber-900 font-medium">
          {t('miembrosEspacio.noActivado.titulo')}
        </p>
        <p className="text-xs text-amber-900/80 leading-relaxed">
          <Trans
            i18nKey="miembrosEspacio.noActivado.texto"
            components={{ b: <strong /> }}
          />
        </p>
        <button
          type="button"
          onClick={() => onUpgrade('space_base')}
          disabled={upgrading}
          className="inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-md bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {upgrading && <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />}
          {t('miembrosEspacio.noActivado.boton')}
        </button>
      </div>
    )
  }

  // Miembros ILIMITADOS desde el 2026-08-14: desaparece la barra de aforo y
  // el "3 / 5" que había aquí. Un usuario extra cuesta 0 € en Cloudflare; lo
  // que cuesta es el espacio, así que el único indicador con sentido —y el
  // único que empuja a ampliar— es el almacenamiento.
  const storagePct = Math.min(100, (quota.bytesUsed / Math.max(1, quota.bytesLimit)) * 100)
  const near = storagePct > 85

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] text-zinc-500 uppercase tracking-wide">
            {t('miembrosEspacio.cuota.miembros')}
          </p>
          <p className="text-sm font-medium text-zinc-900 tabular-nums">
            {currentMembers}
          </p>
          <p className="text-[11px] text-zinc-500 mt-1">{t('miembrosEspacio.cuota.sinLimite')}</p>
        </div>
        <div>
          <p className="text-[11px] text-zinc-500 uppercase tracking-wide">
            {t('panel.almacenamiento')}
          </p>
          <p className="text-sm font-medium text-zinc-900 tabular-nums">
            {formatBytes(quota.bytesUsed)} / {formatBytes(quota.bytesLimit)}
          </p>
          <div className="mt-1 h-1 w-full bg-zinc-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${storagePct > 85 ? 'bg-amber-500' : 'bg-zinc-900'}`}
              style={{ width: `${storagePct}%` }}
            />
          </div>
        </div>
      </div>
      {/* Aviso, no botón de compra: el producto de ampliación NO existe en
          Polar (decisión 2026-08-18, se lanza con Pro + Espacio). Ofrecer aquí
          un botón llevaría a un checkout que devuelve `invalid_plan`, que es
          la peor experiencia posible justo cuando alguien quiere pagarte.
          Se le dan las dos salidas que SÍ funcionan hoy. */}
      {near && (
        <p className="text-[11px] text-amber-700 leading-relaxed">
          {t('miembrosEspacio.cuota.cercaLimite')}
        </p>
      )}
    </div>
  )
}
